import { 
  Button, 
  Card, 
  Box, 
  Stack, 
  Skeleton, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
  Fade,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Loading from 'components/Loading';
import dayjs from 'dayjs';
import FilterOtCard from 'components/Card/FilterOtCard';
import * as actions from '../../redux/actions';
import OtCheckRe from '../../components/Table/OtCheckRe';
import EditOtModal from '../../components/Modal/EditOtModal';
import { confirmAsync } from '../../utils/functions/globalDialog';
import { canApproveByCategory } from '../../utils/functions/approverAccess';

export default function OtRequest({ title, subtitle }) {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const timestamp = useSelector((state) => state.otRequestOrder);
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const project = useSelector((state) => state.project);
  const canApprove = canApproveByCategory(me, info, 'ot_approver', '/humen/ot-request');

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [dateStart, setDateStart] = useState(dayjs());
  const [projectSelect, setProjectSelect] = useState();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalId, setEditModalId] = useState(null);

  useEffect(() => {
    dispatch(actions.otRequestOrderAll({ size, page }));
    return () => {};
  }, [page, size]);

  useEffect(() => {
    dispatch(actions.projectAll({ lite: true }));
    dispatch(actions.getInformation());
    dispatch(actions.meGet());
    return () => {};
  }, []);

  const renderApprove = async (each) => {
    const datasubmit = {
      ...each,
      status_approve: {
        approve: true,
        approver: me?.userData?.id,
      },
      status: 'APPROVE',
    };
    await dispatch(actions.otRequestOrderPut(each?.id, datasubmit));
    
    // Update local state แทนการ reload
    const updatedRows = timestamp.rows.map(row => 
      row.id === each.id ? { ...row, status: 'APPROVE', status_approve: datasubmit.status_approve } : row
    );
    dispatch({
      type: 'OT_REQUEST_UPDATE_LOCAL',
      payload: { rows: updatedRows, total: timestamp.total }
    });
  };

  const handleAddOt = () => {
    history.push('/humen/ot-request/add');
  };

  const renderReject = async (each) => {
    const cofirm = await confirmAsync('confirm');
    if (cofirm) {
      const datasubmit = {
        ...each,
        status_approve: {
          approve: true,
          approver: me?.userData?.id,
        },
        status: 'REJECT',
      };
      await dispatch(actions.otRequestOrderPut(each?.id, datasubmit));
      
      // Update local state
      const updatedRows = timestamp.rows.map(row => 
        row.id === each.id ? { ...row, status: 'REJECT', status_approve: datasubmit.status_approve } : row
      );
      dispatch({
        type: 'OT_REQUEST_UPDATE_LOCAL',
        payload: { rows: updatedRows, total: timestamp.total }
      });
    }
  };

  const renderReset = async (each) => {
    const cofirm = await confirmAsync('confirm');
    if (cofirm) {
      const datasubmit = {
        ...each,
        status_approve: {
          approve: false,
          approver: me?.userData?.id,
        },
        status: 'PENDING',
      };
      await dispatch(actions.otRequestOrderPut(each?.id, datasubmit));
      
      // Update local state
      const updatedRows = timestamp.rows.map(row => 
        row.id === each.id ? { ...row, status: 'PENDING', status_approve: datasubmit.status_approve } : row
      );
      dispatch({
        type: 'OT_REQUEST_UPDATE_LOCAL',
        payload: { rows: updatedRows, total: timestamp.total }
      });
    }
  };

  const renderCancel = async (each) => {
    const datasubmit = {
      ...each,
      status_approve: {
        approve: true,
        approver: me?.userData?.id,
      },
      status: 'CANCEL',
    };
    await dispatch(actions.otRequestOrderPut(each?.id, datasubmit));
    
    // Update local state
    const updatedRows = timestamp.rows.map(row => 
      row.id === each.id ? { ...row, status: 'CANCEL', status_approve: datasubmit.status_approve } : row
    );
    dispatch({
      type: 'OT_REQUEST_UPDATE_LOCAL',
      payload: { rows: updatedRows, total: timestamp.total }
    });
  };

  const renderEditButton = (each) => {
    history.push(`/humen/ot-request/edit/${each?.id}`);
  };

  const handleDoubleClickRow = (id) => {
    setEditModalId(id);
    setEditModalOpen(true);
  };

  const renderSkeletonTable = () => (
    <Fade in={true} timeout={300}>
      <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={120} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={120} animation="wave" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                <TableRow key={row}>
                  <TableCell><Skeleton variant="text" width={40} animation="wave" /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="circular" width={32} height={32} animation="wave" />
                      <Box>
                        <Skeleton variant="text" width={100} animation="wave" />
                        <Skeleton variant="text" width={80} height={12} animation="wave" />
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={70} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={80} height={24} animation="wave" /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Skeleton variant="rounded" width={32} height={32} animation="wave" />
                      <Skeleton variant="rounded" width={32} height={32} animation="wave" />
                      <Skeleton variant="rounded" width={32} height={32} animation="wave" />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Skeleton variant="rounded" width={300} height={36} animation="wave" />
        </Box>
      </Card>
    </Fade>
  );

  const renderTaableCheck = () => {
    if (timestamp?.isLoading) {
      return renderSkeletonTable();
    }
    
    return (
      <div>
        <OtCheckRe
          timestamp={timestamp}
          renderApprove={renderApprove}
          renderReject={renderReject}
          renderCancel={renderCancel}
          renderEditButton={renderEditButton}
          renderReset={renderReset}
          onDoubleClickRow={handleDoubleClickRow}
          page={page}
          setPage={setPage}
          size={size}
          setSize={setSize}
          canApprove={canApprove}
        />
      </div>
    );
  };

  const renderFillter = () => (
    <FilterOtCard
      setDateStart={setDateStart}
      projectSelect={projectSelect}
      setProjectSelect={setProjectSelect}
      project={project}
      dateStart={dateStart}
    />
  );

  const renderButtonAdd = () => (
    <Button variant="contained" onClick={handleAddOt}>
      เพิ่มคำขอ OT
    </Button>
  );

  return (
    <div>
      {/* <div>{renderFillter()}</div> */}
      <div className="flex justify-end py-1">{renderButtonAdd()}</div>
      <div>{renderTaableCheck()}</div>
      {/* <div>{renderTableClaim()}</div> */}
      <EditOtModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        id={editModalId}
        onRefresh={() => dispatch(actions.otRequestOrderAll({ size, page }))}
      />
    </div>
  );
}
